import {
  AppliedApplicationRes,
  Meeting,
  RegisterPayload,
  UpdateUserPayload,
  User,
  UserDocumentRes,
} from "@/store/types/userTypes";
import axiosInstance from "@/utils/axiosInstance";
import { createAsyncThunk } from "@reduxjs/toolkit";
import { startLoading, stopLoading } from "../loader/loaderSlice";
import axios from "axios";

const normalizeOptionalName = (value?: string | null): string | undefined => {
  if (typeof value !== "string") return undefined;
  const cleaned = value.trim().replace(/\s+/g, " ");
  if (!cleaned) return undefined;
  return /^[A-Za-z ]{2,50}$/.test(cleaned) ? cleaned : undefined;
};

export const getUserDetail = createAsyncThunk<User, number | string | string[]>(
  "user/getUserDetail",
  async (userId, { rejectWithValue, dispatch }) => {
    dispatch(startLoading());
    try {
      const res = await axiosInstance.get(`/students/${userId}`);
      dispatch(stopLoading());
      return res.data;
    } catch (e) {
      dispatch(stopLoading());
      return rejectWithValue(e);
    }
  },
);

export const verifyOtp = createAsyncThunk<
  User,
  { phoneNumber: string; otp: string }
>("user/verifyOtp", async (payload, { rejectWithValue }) => {
  try {
    const res = await axiosInstance.post(`mobile-otp/verify-otp`, {
      phoneNumber: payload.phoneNumber,
      otp: payload.otp,
    });
    return res.data;
  } catch (e) {
    return rejectWithValue(e);
  }
});

export const sentOtp = createAsyncThunk<
  User,
  { phoneNumber: string; loginOrSignup?: string },
  { rejectValue: { statusCode: number; message: string; errorCode?: string } }
>("user/sendOtp", async (payload, { rejectWithValue }) => {
  try {
    const res = await axiosInstance.post(`mobile-otp/send-otp`, {
      phoneNumber: payload.phoneNumber,
      registerPhoneNumber: payload.loginOrSignup,
    });
    return res.data;
  } catch (err) {
    if (axios.isAxiosError(err) && err.response?.data) {
      return rejectWithValue(err.response.data);
    }
    return rejectWithValue({
      statusCode: 500,
      message: "Unexpected error occurred ",
    });
  }
});

export const updatePrimaryPhone = createAsyncThunk<
  User,
  { phoneNumber: string; userId: string }
>("user/updatePrimaryPhone", async (payload, { rejectWithValue }) => {
  try {
    const res = await axiosInstance.patch(
      `students/update/primary/phone/number/${payload.userId}`,
      {
        updatedPhoneNumber: payload.phoneNumber,
      },
    );
    return res.data;
  } catch (e) {
    return rejectWithValue(e);
  }
});

export const registerUser = createAsyncThunk<User, RegisterPayload>(
  "user/registerUser",
  async (payload, { rejectWithValue }) => {
    try {
      const res = await axiosInstance.post(`students/register`, payload);
      return res.data;
    } catch (e) {
      return rejectWithValue(e);
    }
  },
);
export interface UpdateProfileRes {
  user?: User | null;
  status: boolean;
}

export const updateUserDetails = createAsyncThunk<
  UpdateProfileRes,
  UpdateUserPayload
>("user/updateUserDetails", async ({ id, payload }, { dispatch }) => {
  dispatch(startLoading());
  try {
    const res = await axiosInstance.patch(`students/update/${id}`, payload);
    dispatch(stopLoading());
    return { status: true, user: res.data.data };
  } catch (e) {
    console.error("Error", e);
    dispatch(stopLoading());
    return { status: false };
  }
});

export const deleteUserDocument = createAsyncThunk<
  UserDocumentRes,
  number | string | string[]
>("user/deleteUserDocument", async (docId, { rejectWithValue, dispatch }) => {
  dispatch(startLoading());
  try {
    const res = await axiosInstance.delete(`students/delete/${docId}`);
    dispatch(stopLoading());
    return res.data;
  } catch (e) {
    dispatch(stopLoading());
    return rejectWithValue(e);
  }
});

export const getUploadedDocument = createAsyncThunk<
  UserDocumentRes,
  number | string | string[]
>("user/getUploadedDocument", async (userId, { rejectWithValue }) => {
  try {
    const res = await axiosInstance.get(`/documents/${userId}`);

    return res.data;
  } catch (e) {
    return rejectWithValue(e);
  }
});

export const getAppliedApplications = createAsyncThunk<
  AppliedApplicationRes,
  number | string | string[]
>("user/getApplications", async (userId, { rejectWithValue, dispatch }) => {
  dispatch(startLoading());
  try {
    const { data } = await axiosInstance.get(
      `/student-applications/getApplications/${userId}`,
    );
    dispatch(stopLoading());
    return { data: data.data };
  } catch (e) {
    dispatch(stopLoading());
    return rejectWithValue(e);
  }
});

export const getUserMeeting = createAsyncThunk<
  Meeting,
  number | string | string[]
>("user/getUserMeeting", async (userId, { rejectWithValue, dispatch }) => {
  dispatch(startLoading());
  try {
    const res = await axiosInstance.get(
      `/meetings/${userId}?search=searchingFor=student`,
    );
    dispatch(stopLoading());
    return res.data;
  } catch (e) {
    dispatch(stopLoading());
    return rejectWithValue(e);
  }
});

export const sendVerificationEmail = createAsyncThunk<
  { status: number; message: string },
  { email: string; phoneNumber?: string | null; redirectTo?: string | null }
>("user/sendVerificationEmail", async (payload, { rejectWithValue }) => {
  try {
    const res = await axiosInstance.post(
      `users/send/email-verification`,
      payload,
    );
    return res.data;
  } catch (e) {
    return rejectWithValue(e);
  }
});

export const getDocumentUrl = createAsyncThunk<
  { url: string; filename: string },
  number | string | string[]
>("user/getDocumentUrl", async (docId, { rejectWithValue, dispatch }) => {
  dispatch(startLoading());
  try {
    const res = await axiosInstance.get(`/documents/${docId}`);
    dispatch(stopLoading());
    return { url: res.data.signed_url, filename: res.data.filename };
  } catch (e) {
    dispatch(stopLoading());
    return rejectWithValue(e);
  }
});

export const resetPassword = createAsyncThunk<string, { email: string }>(
  "user/resetPassword",
  async (payload, { rejectWithValue }) => {
    try {
      const res = await axiosInstance.post(
        `users/send/reset-password`,
        payload,
      );
      return res.data;
    } catch (e) {
      return rejectWithValue(e);
    }
  },
);
